import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ExpertManagerEventNotificationRecord,
  ExpertManagerEventNotificationService,
  ExpertManagerEventNotificationType,
  ReadExpertManagerEventNotificationRequest,
  SliceExpertManagerEventNotificationRecord,
} from '@libs/shared/api';
import { PageQuery } from '@libs/shared/interface/page.interface';
import { extractErrorMessage } from '@libs/shared/utils/functions/error-message.util';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface ExpertManagerEventNotificationCoreQueryFilter {
  active?: boolean;
  read?: boolean;
  types?: ExpertManagerEventNotificationType[];
}

interface ExpertManagerEventNotificationCoreState {
  slice: SliceExpertManagerEventNotificationRecord;
  pageQuery: PageQuery<ExpertManagerEventNotificationCoreQueryFilter>;
  isLoading: boolean;
  isLoaded: boolean;
  errorMessage: string;
  unreadCount: number;
}

const initialState: ExpertManagerEventNotificationCoreState = {
  slice: undefined,
  pageQuery: {
    filter: {
      active: true,
    },
    page: 0,
    size: 25,
    sort: undefined,
  },
  isLoading: false,
  isLoaded: false,
  errorMessage: '',
  unreadCount: 0,
};

@Injectable({
  providedIn: 'root',
})
export class ExpertManagerEventNotificationCoreStore {
  readonly #expertEventManagerNotificationService = inject(
    ExpertManagerEventNotificationService
  );

  readonly state = signalState(initialState);
  readonly slice$$ = this.state.slice;
  readonly pageQuery$$ = this.state.pageQuery;
  readonly isLoading$$ = this.state.isLoading;
  readonly isLoaded$$ = this.state.isLoaded;
  readonly errorMessage$$ = this.state.errorMessage;
  readonly unreadCount$$ = this.state.unreadCount;

  constructor() {}

  updateExpertManagerEventNotificationPageQuery(
    pageQuery: ExpertManagerEventNotificationCoreQueryFilter
  ) {
    patchState(this.state, {
      pageQuery: {
        ...this.state().pageQuery,
        filter: pageQuery,
      },
    });
    this.fetchExpertManagerEventNotificationSlice(pageQuery);
  }

  fetchExpertManagerEventNotificationSlice =
    rxMethod<ExpertManagerEventNotificationCoreQueryFilter>(
      pipe(
        tap(payload => {
          patchState(this.state, {
            ...this.state(),
            pageQuery: {
              ...this.state().pageQuery,
              page: 0,
              filter: payload,
            },
            isLoading: true,
          });
        }),
        switchMap(() =>
          this.#expertEventManagerNotificationService
            .fetchExpertManagerEventNotificationSlice(
              this.pageQuery$$().filter.active,
              this.pageQuery$$().filter.read,
              this.pageQuery$$().filter.types,
              this.pageQuery$$().page,
              this.pageQuery$$().size,
              [this.pageQuery$$().sort?.toString()]
            )
            .pipe(
              tapResponse(
                (slice: SliceExpertManagerEventNotificationRecord) => {
                  patchState(this.state, {
                    slice,
                    isLoading: false,
                    isLoaded: true,
                    errorMessage: '',
                  });
                },
                (e: HttpErrorResponse) => {
                  patchState(this.state, {
                    slice: undefined,
                    isLoading: false,
                    isLoaded: false,
                    errorMessage: extractErrorMessage(e),
                  });
                }
              )
            )
        )
      )
    );

  fetchNextExpertManagerEventNotificationSlice = rxMethod<void>(
    pipe(
      tap(() => {
        patchState(this.state, {
          ...this.state(),
          isLoading: true,
        });
      }),
      switchMap(() =>
        this.#expertEventManagerNotificationService
          .fetchExpertManagerEventNotificationSlice(
            this.pageQuery$$().filter.active,
            this.pageQuery$$().filter.read,
            this.pageQuery$$().filter.types,
            this.pageQuery$$().page + 1,
            this.pageQuery$$().size,
            [this.pageQuery$$().sort?.toString()]
          )
          .pipe(
            tapResponse(
              (slice: SliceExpertManagerEventNotificationRecord) => {
                patchState(this.state, {
                  ...this.state(),
                  slice: {
                    ...slice,
                    content: [...this.slice$$().content, ...slice.content],
                  },
                  isLoading: false,
                  isLoaded: true,
                  errorMessage: '',
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  slice: undefined,
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  fetchExpertManagerEventNotificationById = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .fetchExpertManagerEventNotificationById(payload.id)
          .pipe(
            tapResponse(
              (record: ExpertManagerEventNotificationRecord) => {
                patchState(this.state, {
                  slice: {
                    ...this.slice$$(),
                    content: this.slice$$().content.map(item =>
                      item.id === record.id ? record : item
                    ),
                  },
                  isLoading: false,
                  isLoaded: true,
                  errorMessage: '',
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  fetchExpertMangerEventNotificationUnreadCount = rxMethod<void>(
    pipe(
      switchMap(() =>
        this.#expertEventManagerNotificationService
          .fetchExpertManagerEventNotificationUnreadCount()
          .pipe(
            tapResponse(
              (unreadCount: number) => {
                patchState(this.state, {
                  unreadCount,
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  unreadCount: 0,
                });
              }
            )
          )
      )
    )
  );

  readExpertManagerEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .readExpertManagerEventNotification(payload.id)
          .pipe(
            tapResponse(
              () => {
                patchState(this.state, {
                  slice: {
                    ...this.slice$$(),
                    content: this.slice$$().content.map(item =>
                      item.id === payload.id ? { ...item, read: true } : item
                    ),
                  },
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  unreadExpertManagerEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .unreadExpertManagerEventNotification(payload.id)
          .pipe(
            tapResponse(
              () => {
                patchState(this.state, {
                  slice: {
                    ...this.slice$$(),
                    content: this.slice$$().content.map(item =>
                      item.id === payload.id ? { ...item, read: false } : item
                    ),
                  },
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  readAllExpertManagerEventNotifications = rxMethod<{
    request: ReadExpertManagerEventNotificationRequest;
  }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .readAllExpertManagerEventNotifications(payload.request)
          .pipe(
            tapResponse(
              () => {
                const updated = this.state().slice?.content.map(item =>
                  item.readAt
                    ? item
                    : { ...item, readAt: new Date().toISOString() }
                );

                patchState(this.state, {
                  ...this.state(),
                  slice: {
                    ...this.state().slice,
                    content: updated,
                  },
                });
              },
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  activateExpertManagerEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .activateExpertManagerEventNotification(payload.id)
          .pipe(
            tapResponse(
              (id: string) =>
                this.fetchExpertManagerEventNotificationById({ id }),
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );

  inactivateExpertManagerEventNotification = rxMethod<{ id: string }>(
    pipe(
      switchMap(payload =>
        this.#expertEventManagerNotificationService
          .inactivateExpertManagerEventNotification(payload.id)
          .pipe(
            tapResponse(
              (id: string) =>
                this.fetchExpertManagerEventNotificationById({ id }),
              (e: HttpErrorResponse) => {
                patchState(this.state, {
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );
}
